import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  useToast,
  Box,
  Text,
  Avatar,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
  IconButton,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { COLLECTION_NAMES, User } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { FiX } from 'react-icons/fi';

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated: (conversationId: string) => void;
  createDirectMessage: (otherUserId: string) => Promise<string>;
  createGroupChat: (memberIds: string[], name?: string) => Promise<string>;
}

const directMessageSchema = Yup.object({
  searchQuery: Yup.string().min(1, 'Enter a name to search'),
});

const groupChatSchema = Yup.object({
  name: Yup.string().max(50, 'Group name must be 50 characters or less'),
  searchQuery: Yup.string(),
});

export function NewConversationModal({
  isOpen,
  onClose,
  onConversationCreated,
  createDirectMessage,
  createGroupChat,
}: NewConversationModalProps) {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [tabIndex, setTabIndex] = useState(0);

  // Load users
  useEffect(() => {
    const usersQuery = query(
      collection(db, COLLECTION_NAMES.USERS),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const usersData = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as User[];

      // Filter out current user
      const filtered = usersData.filter(
        (user) => user.id !== currentUser?.uid && user.id !== currentUser?.id
      );

      setUsers(filtered);
      setFilteredUsers(filtered);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Reset selections when modal opens/closes or tab changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUserId(null);
      setSelectedMemberIds([]);
      setTabIndex(0);
    }
  }, [isOpen]);

  const handleSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        (user.name?.toLowerCase().includes(query) ||
          user.Name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.Email?.toLowerCase().includes(query)) &&
        user.id !== currentUser?.uid &&
        user.id !== currentUser?.id
    );

    setFilteredUsers(filtered);
  };

  const toggleMemberSelection = (userId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateDirectMessage = async () => {
    if (!selectedUserId) {
      toast({
        title: 'Error',
        description: 'Please select a user',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const conversationId = await createDirectMessage(selectedUserId);
      toast({
        title: 'Conversation created',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onConversationCreated(conversationId);
      onClose();
      setSelectedUserId(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create conversation',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateGroupChat = async (values: { name?: string }) => {
    if (selectedMemberIds.length < 1) {
      toast({
        title: 'Error',
        description: 'Please select at least one member',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const conversationId = await createGroupChat(
        selectedMemberIds,
        values.name?.trim() || undefined
      );
      toast({
        title: 'Group chat created',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onConversationCreated(conversationId);
      onClose();
      setSelectedMemberIds([]);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create group chat',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getSelectedUsers = () => {
    return users.filter((user) => selectedMemberIds.includes(user.id));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Conversation</ModalHeader>
        <ModalCloseButton />
        <Tabs index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab>Direct Message</Tab>
            <Tab>Group Chat</Tab>
          </TabList>

          <TabPanels>
            {/* Direct Message Tab */}
            <TabPanel>
              <Formik
                initialValues={{ searchQuery: '' }}
                validationSchema={directMessageSchema}
                onSubmit={() => {}}
              >
                {({ values, setFieldValue }) => (
                  <Form>
                    <VStack spacing={4} align="stretch">
                      <Field name="searchQuery">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched}>
                            <FormLabel>Search for a user</FormLabel>
                            <Input
                              {...field}
                              placeholder="Type a name or email..."
                              onChange={(e) => {
                                setFieldValue('searchQuery', e.target.value);
                                handleSearch(e.target.value);
                              }}
                            />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      <Box
                        maxH="300px"
                        overflowY="auto"
                        borderWidth="1px"
                        borderRadius="md"
                        p={2}
                      >
                        {filteredUsers.length === 0 ? (
                          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                            No users found
                          </Text>
                        ) : (
                          <VStack spacing={2} align="stretch">
                            {filteredUsers.map((user) => {
                              const userName = user.name || user.Name || 'Unknown';
                              const userEmail = user.email || user.Email || '';
                              const isSelected = selectedUserId === user.id;

                              return (
                                <Box
                                  key={user.id}
                                  p={2}
                                  borderRadius="md"
                                  bg={isSelected ? 'blue.50' : 'transparent'}
                                  borderWidth={isSelected ? '2px' : '1px'}
                                  borderColor={isSelected ? 'blue.500' : 'gray.200'}
                                  cursor="pointer"
                                  _hover={{ bg: 'gray.50' }}
                                  onClick={() => setSelectedUserId(user.id)}
                                >
                                  <HStack spacing={3}>
                                    <Avatar
                                      size="sm"
                                      src={user.photo || user.photoURL}
                                      name={userName}
                                    />
                                    <VStack align="flex-start" spacing={0} flex="1">
                                      <Text fontSize="sm" fontWeight="medium">
                                        {userName}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {userEmail}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                </Box>
                              );
                            })}
                          </VStack>
                        )}
                      </Box>
                    </VStack>

                    <ModalFooter mt={4}>
                      <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="blue"
                        onClick={handleCreateDirectMessage}
                        isDisabled={!selectedUserId}
                      >
                        Start Conversation
                      </Button>
                    </ModalFooter>
                  </Form>
                )}
              </Formik>
            </TabPanel>

            {/* Group Chat Tab */}
            <TabPanel>
              <Formik
                initialValues={{ name: '', searchQuery: '' }}
                validationSchema={groupChatSchema}
                onSubmit={handleCreateGroupChat}
              >
                {({ values, setFieldValue, handleSubmit }) => (
                  <Form>
                    <VStack spacing={4} align="stretch">
                      <Field name="name">
                        {({ field, meta }: any) => (
                          <FormControl isInvalid={meta.error && meta.touched}>
                            <FormLabel>Group Name (Optional)</FormLabel>
                            <Input
                              {...field}
                              placeholder="Enter a group name..."
                              maxLength={50}
                            />
                            <FormErrorMessage>{meta.error}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>

                      {/* Selected Members */}
                      {selectedMemberIds.length > 0 && (
                        <Box>
                          <FormLabel fontSize="sm" mb={2}>
                            Selected Members ({selectedMemberIds.length})
                          </FormLabel>
                          <HStack spacing={2} flexWrap="wrap">
                            {getSelectedUsers().map((user) => {
                              const userName = user.name || user.Name || 'Unknown';
                              return (
                                <Badge
                                  key={user.id}
                                  colorScheme="blue"
                                  borderRadius="full"
                                  px={3}
                                  py={1}
                                  display="flex"
                                  alignItems="center"
                                  gap={2}
                                >
                                  {userName}
                                  <IconButton
                                    aria-label="Remove member"
                                    size="xs"
                                    variant="ghost"
                                    icon={<FiX />}
                                    onClick={() => toggleMemberSelection(user.id)}
                                  />
                                </Badge>
                              );
                            })}
                          </HStack>
                        </Box>
                      )}

                      <Field name="searchQuery">
                        {({ field, meta }: any) => (
                          <FormControl>
                            <FormLabel>Add Members</FormLabel>
                            <Input
                              {...field}
                              placeholder="Type a name or email..."
                              onChange={(e) => {
                                setFieldValue('searchQuery', e.target.value);
                                handleSearch(e.target.value);
                              }}
                            />
                          </FormControl>
                        )}
                      </Field>

                      <Box
                        maxH="300px"
                        overflowY="auto"
                        borderWidth="1px"
                        borderRadius="md"
                        p={2}
                      >
                        {filteredUsers.length === 0 ? (
                          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                            No users found
                          </Text>
                        ) : (
                          <VStack spacing={2} align="stretch">
                            {filteredUsers
                              .filter((user) => !selectedMemberIds.includes(user.id))
                              .map((user) => {
                                const userName = user.name || user.Name || 'Unknown';
                                const userEmail = user.email || user.Email || '';

                                return (
                                  <Box
                                    key={user.id}
                                    p={2}
                                    borderRadius="md"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    cursor="pointer"
                                    _hover={{ bg: 'gray.50' }}
                                    onClick={() => toggleMemberSelection(user.id)}
                                  >
                                    <HStack spacing={3}>
                                      <Avatar
                                        size="sm"
                                        src={user.photo || user.photoURL}
                                        name={userName}
                                      />
                                      <VStack align="flex-start" spacing={0} flex="1">
                                        <Text fontSize="sm" fontWeight="medium">
                                          {userName}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {userEmail}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </Box>
                                );
                              })}
                          </VStack>
                        )}
                      </Box>
                    </VStack>

                    <ModalFooter mt={4}>
                      <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        colorScheme="blue"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                        isDisabled={selectedMemberIds.length < 1}
                      >
                        Create Group Chat
                      </Button>
                    </ModalFooter>
                  </Form>
                )}
              </Formik>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ModalContent>
    </Modal>
  );
}
